<template>
  <div class="app-mapviewer">
    <MapviewerToolbox
      v-show="
        !showFeatureAnalytic && !isAttachmentsFeatureFullscreen && !showFeatureProjectAnalysis
      "
      @toggleToolboxMenu="checkIfToolboxMenuIsExpanded"
    />
    <MapView :error="error" :initialFetchLoading="initialFetchLoading" />
    <FloorButtons
      v-if="(isPlanviewShowing || isModelviewShowing) && !isDrawPointActive"
      :buttons="floorbuttons"
      :activeValue="selectedLevelNumber"
      @floorChange="handleClickOnFloorButton"
    />
    <ViewButtons />
  </div>
</template>

<script>
import FloorButtons from '@/components/general/FloorButtons.vue'
import ViewButtons from '@/components/general/ViewButtons/ViewButtons.vue'
import MapviewerToolbox from '@/components/mapviewer/MapviewerToolbox.vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { mapActions as mapActionsPinia } from 'pinia'
import { MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { CHANGE_LEVEL } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import MapView from '@/components/map/MapView.vue'
import { mapState as mapStatePinia } from 'pinia'
import { useGeneralStore } from '@/pinia'
import { generateWmsBoundingBoxes, getWmsList } from '@/services/wms.service'
import { getRealmId } from '@/utils/realmId'
import { useSelectionStore } from '@/pinia/selectionNew'

export default {
  name: 'MapviewerView',
  components: {
    FloorButtons,
    MapviewerToolbox,
    ViewButtons,
    MapView
  },
  props: {
    error: {
      validator: (prop) => typeof prop === 'string' || prop === null,
      default: ''
    },
    initialFetchLoading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const selection = useSelectionStore()
    return {
      selection
    }
  },
  data() {
    return {
      isToolBoxMenuExpanded: false
    }
  },
  computed: {
    ...mapState({
      clickedItemType: (state) => state.clickedItemType,
      levelsForSelectedBuilding: (state) => state.levelsForSelectedBuilding,
      mapIsLoaded: (state) => state.mapIsLoaded,
      selectedLevel: (state) => state.selectedLevel,
      showPoiMenu: (state) => state.showPoiMenu,
      showZoneMenu: (state) => state.showZoneMenu,
      mobileScreen: (state) => state.mobileScreen
    }),
    ...mapStatePinia(useGeneralStore, [
      'isFeatureFullscreen',
      'showFeatureAnalytic',
      'showFeatureAttachments',
      'showFeatureProjectAnalysis',
      'showFeatureErrands'
    ]),
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding
    }),
    ...mapGetters({
      isDrawPointActive: 'isDrawPointActive',
      isModelviewShowing: 'isModelviewShowing',
      isPlanviewShowing: 'isPlanviewShowing',
      selectedLevelNumber: 'selectedLevelNumber'
    }),
    floorbuttons() {
      const floorbuttonsOnPlanview = []
      if (!this.levelsForSelectedBuilding) return floorbuttonsOnPlanview
      for (let i = 0; i < this.levelsForSelectedBuilding.data.features.length; i++) {
        floorbuttonsOnPlanview.push({
          character: this.levelsForSelectedBuilding.data.features[i].properties.level,
          emit: [
            this.levelsForSelectedBuilding.data.features[i].properties.id,
            this.levelsForSelectedBuilding.data.features[i].properties.level
          ]
        })
      }
      return floorbuttonsOnPlanview.reverse()
    },
    isAttachmentsFeatureFullscreen() {
      return this.showFeatureAttachments && this.isFeatureFullscreen
    },
    realmId() {
      return getRealmId()
    }
  },
  watch: {
    mapIsLoaded: {
      immediate: true,
      async handler() {
        try {
          const wmss = await getWmsList(this.realmId)
          const wmsLayers = wmss.map((wms) => ({
            ...wms,
            checked: false,
            id: wms.attachmentId,
            visible: false
          }))
          const wmssWithBoundingBox = await generateWmsBoundingBoxes(wmsLayers)
          this.setFilteredWmsLayers(wmssWithBoundingBox)
        } catch (e) {
          this.setFilteredWmsLayers([])
        }
      }
    }
  },
  methods: {
    ...mapActions({
      changeLevel: CHANGE_LEVEL
    }),
    ...mapActions(PLANVIEW_PREFIX, {
      loadSensorData: 'loadSensorData'
    }),
    ...mapActionsPinia(useGeneralStore, ['setFilteredWmsLayers']),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE,
      setToolboxMenuActive: 'setToolboxMenuActive'
    }),
    checkIfToolboxMenuIsExpanded(value) {
      this.isToolBoxMenuExpanded = value
      this.setToolboxMenuActive(this.isToolBoxMenuExpanded)
    },
    async handleClickOnFloorButton([id, levelNumber]) {
      this.selection.set([id])
      this.commitClick({ type: 'level' })
      if (this.selectedLevelNumber === levelNumber) return
      await this.changeLevel({ levelNumber })
      this.loadSensorData()
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'src/design-system/design-tokens/variables.scss';
@import 'src/styles/variables.scss';

.app-mapviewer {
  background-color: $color-grey-light;
  margin-top: 64px;
  height: calc(100% - #{variables.$layout-top-bar-height});
  width: $width-mapviewer;
  max-width: $width-mapviewer;
}

@media screen and (max-width: 1024px) {
  .app-mapviewer {
    z-index: 10;
    position: relative;
    height: calc(100vh - #{variables.$layout-top-bar-height});
    max-width: 100%;
    width: 100%;
  }
}
</style>
